import { useCallback, useEffect, useRef } from 'react';

import { useConfig } from '@/shared/contexts/config';
import {
  postAdminRefreshToken,
  postUserRefreshToken,
} from '@/shared/api/cloud';

const REFRESH_BEFORE_MS = 3 * 60 * 1000;
const AUTH_EXPIRES = 'AUTH_EXPIRES';

const getExpiration = () => {
  const value = localStorage.getItem(AUTH_EXPIRES);

  if (!value) {
    return null;
  }

  return new Date(Number(value)).getTime();
};

const setExpiration = (newTime: number) => {
  localStorage.setItem(AUTH_EXPIRES, newTime.toString());
};

const getDelay = (expiration: number) => {
  return expiration - Date.now() - REFRESH_BEFORE_MS;
};

export const RefreshToken = () => {
  const { appName } = useConfig();
  const isAdminConsole = appName === 'adminconsole';
  const apiCall = isAdminConsole ? postAdminRefreshToken : postUserRefreshToken;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiCall();
      const { session_expiration_time } = data;
      const expirationTime = new Date(session_expiration_time).getTime();

      setExpiration(expirationTime);

      const delay = getDelay(expirationTime);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => void refresh(), delay);
    } catch (err) {
      console.error(err);
    }
  }, [apiCall]);

  useEffect(() => {
    const storedExpiration = getExpiration();

    if (!storedExpiration) {
      void refresh();
      return;
    }

    const delay = getDelay(storedExpiration);

    if (delay <= 0) {
      void refresh();
    } else {
      timeoutRef.current = setTimeout(() => void refresh(), delay);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [refresh]);

  return null;
};
